import React, { useState } from 'react';
import {
    List,
    ListItem,
    ListItemText,
    IconButton,
    Box,
    Typography,
    Chip,
    Tooltip,
    Stack,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button
} from '@mui/material';
import {
    AccountWhiteIcon,
    AccountIcon
} from '../../../../assets/icons';
import {
    AdminPanelSettings as AdminPanelSettingsIcon,
    ManageAccounts as ManageAccountsIcon,
    SwapHoriz as TransferCreatorIcon
} from '@mui/icons-material';
import './TeamList.css';
import { useNotification } from '../../../../components/index';

export const TeamList = ({
    team,
    currentUserAccess,
    onEditUserAssess,
    onDeleteUserAccess,
    onUserTaskAccessDeleted,
    onTransferCreatorRole,
    onSendNotifications,
    theme,
    isReadonly = false,
    onCreatorTransferred = null
}) => {
    const [deleting, setDeleting] = useState(false);
    const [transferring, setTransferring] = useState(false);
    const [confirmTransfer, setConfirmTransfer] = useState(null);
    const {
        showSuccess,
        showError
    } = useNotification();

    const canModifyUser = (targetUserTaskAccess) => {
        if (!currentUserAccess?.canManageTeam) return false;
        if (targetUserTaskAccess.isCurrentUser) return false;
        if (targetUserTaskAccess.role === 'Creator') return false;
        return true;
    };

    const canTransferCreator = (targetUserTaskAccess) => {
        // Только текущий создатель может передать роль создателя
        // И нельзя передать самому себе
        return currentUserAccess?.role === 'Creator' &&
            !targetUserTaskAccess.isCurrentUser &&
            targetUserTaskAccess.role !== 'Creator';
    };

    const handleDeleteUserAccess = async (userTaskAccess) => {
        setDeleting(true);
        try {
            const isDeleted = await onDeleteUserAccess(userTaskAccess.id);
            if (isDeleted) {
                await onUserTaskAccessDeleted();
            }
        } catch (error) {
            console.error('Ошибка удаления прав пользователя:', error);
            showError('Ошибка удаления прав пользователя');
        } finally {
            setDeleting(false);
        }

        if (userTaskAccess.user?.login && onSendNotifications) {
            onSendNotifications([userTaskAccess.user.login], 'access_removed');
        }
    };

    const handleTransferCreatorClick = (event, userTaskAccess) => {
        setConfirmTransfer(userTaskAccess);
    };

    const handleTransferCreatorConfirm = async () => {
        if (!confirmTransfer) return;

        setTransferring(true);
        try {
            await onTransferCreatorRole(confirmTransfer);

            setConfirmTransfer(null);
            showSuccess('Роль создателя успешно передана');

            if (onCreatorTransferred) {
                onCreatorTransferred();
            }
        } catch (error) {
            console.error('Ошибка передачи роли создателя:', error);
            showError('Ошибка передачи роли создателя');
        } finally {
            setTransferring(false);
        }

        if (confirmTransfer.user?.login && onSendNotifications) {
            onSendNotifications([confirmTransfer.user.login], 'creator_transferred');
        }
    };

    const handleTransferCancel = () => {
        setConfirmTransfer(null);
    };

    const renderUserInfo = (userTaskAccess) => {
        return (
            <Stack direction="row" spacing={2} alignItems="center">
                <Stack direction="row" spacing={2} alignItems="center" sx={{ width: "30%", minWidth: 330 }}>
                    {theme === 'light' ? <AccountIcon className="account-icon bg-border" /> : <AccountWhiteIcon className="account-icon bg-border" />}
                    <ListItemText
                        primary={
                            <Stack direction="row" spacing={1} alignItems="center">
                                <span>
                                    {userTaskAccess.user.fullName || userTaskAccess.user.login}
                                </span>
                                {userTaskAccess.canManageTeam && (
                                    <Tooltip title="Может управлять командой">
                                        <AdminPanelSettingsIcon
                                            fontSize="small"
                                            color="action"
                                            className="ml-1 team-manager"
                                        />
                                    </Tooltip>
                                )}
                            </Stack>
                        }
                        secondary={
                            <>
                                <span className="email">
                                    {userTaskAccess.user.login}
                                </span>
                            </>
                        }
                    />
                </Stack>
                <Chip
                    label={userTaskAccess.roleDisplayName}
                    className={`chip-${userTaskAccess.role.toLowerCase()} chip-role`}
                    size="small"
                />
            </Stack>
        )
    };

    if (team.length === 0) {
        return (
            <Typography align="center" className="mt-3">
                В команде пока никого нет
            </Typography>
        );
    }

    return (
        <>
            <List>
                {team.map((userTaskAccess) => {
                    return (
                        <ListItem
                            key={userTaskAccess.user.guid}
                            divider
                            className="not-selecting team-member-item"
                            sx={{
                                backgroundColor: userTaskAccess.isCurrentUser ? 'rgba(25, 118, 210, 0.04)' : 'transparent',
                                borderLeft: userTaskAccess.isCurrentUser ? '3px solid var(--DT1520-Red)' : '3px solid transparent'
                            }}>
                            {userTaskAccess.isCurrentUser ? (
                                <Tooltip title="Вы" placement="right">
                                    {renderUserInfo(userTaskAccess)}
                                </Tooltip>) :
                                (
                                    renderUserInfo(userTaskAccess)
                                )
                            }

                            <Box className="ml-auto">
                                {!isReadonly && canTransferCreator(userTaskAccess) && (
                                    <Tooltip title="Передать роль владельца">
                                        <IconButton
                                            onClick={(e) => handleTransferCreatorClick(e, userTaskAccess)}
                                            size="small"
                                        >
                                            <TransferCreatorIcon className="edit-access" />
                                        </IconButton>
                                    </Tooltip>
                                )}

                                {/* Остальные действия */}
                                {!isReadonly && canModifyUser(userTaskAccess) && (
                                    <>
                                        <IconButton
                                            onClick={(e) => onEditUserAssess(e, userTaskAccess)}
                                            size="small"
                                        >
                                            <ManageAccountsIcon className="edit-access" />
                                        </IconButton>
                                        <IconButton
                                            onClick={() => handleDeleteUserAccess(userTaskAccess)}
                                            size="small"
                                            disabled={deleting}
                                        >
                                            {deleting
                                                ? (
                                                    <span className="material-symbols-outlined icon delete-icon">
                                                        auto_delete
                                                    </span>
                                                )
                                                : (
                                                    <span className="material-symbols-outlined icon delete-icon">
                                                        delete
                                                    </span>
                                                )}
                                        </IconButton>
                                    </>
                                )}
                            </Box>
                        </ListItem>
                    )
                })}
            </List>

            <Dialog
                open={!!confirmTransfer}
                onClose={handleTransferCancel}
                aria-labelledby="transfer-creator-dialog-title"
            >
                <DialogTitle id="transfer-creator-dialog-title">
                    Передача роли владельца
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Вы уверены, что хотите передать роль владельца пользователю{' '}
                        <strong>{confirmTransfer?.user?.fullName || confirmTransfer?.user?.login}</strong>?
                        <br /><br />
                        <strong>Внимание:</strong> После передачи вы станете администратором и не сможете отменить это действие.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={handleTransferCreatorConfirm}
                        disabled={transferring}
                        className="dt1520-button active-dt1520-button view-button blue-button-with-borders"
                    >
                        {transferring ? 'Передача...' : 'Передать роль владельца'}
                    </Button>
                    <Button
                        onClick={handleTransferCancel}
                        disabled={transferring}
                        className="dt1520-button active-dt1520-button view-button default-button">
                        Отмена
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};